import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Users, Hourglass, AlertTriangle, Ticket, MessageCircle, Gift, Lock, Play } from "lucide-react";
import { toast } from "sonner";

const fmtD = (d) => (d ? d.split("-").reverse().join("/") : "");
const waUrl = (phone, text) => {
  const d = (phone || "").replace(/\D/g, "");
  if (!d) return null;
  return `https://wa.me/${d.length >= 12 ? d : `55${d}`}?text=${encodeURIComponent(text)}`;
};

const CARDS = [
  { k: "ativa", label: "Ativas", icon: Play, cls: "border-green-700 text-green-300" },
  { k: "teste", label: "Em teste", icon: Hourglass, cls: "border-sky-700 text-sky-300" },
  { k: "cortesia", label: "Cortesia", icon: Gift, cls: "border-fuchsia-700 text-fuchsia-300" },
  { k: "bloqueada", label: "Bloqueadas", icon: Lock, cls: "border-red-700 text-red-300" },
];

export function BusinessDashboard() {
  const [data, setData] = useState(null);
  const load = async () => { try { const { data: d } = await api.get("/admin/business-dashboard"); setData(d); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { load(); }, []);
  if (!data) return null;
  return (
    <section data-testid="business-dashboard" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display text-2xl font-black uppercase text-zinc-100">Painel do negócio</h2>
        <div className="text-xs text-zinc-400">Faturamento previsto: <strong data-testid="biz-forecast" className="text-green-neon tabular-nums">{brl(data.forecast_revenue)}</strong> / mês</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {CARDS.map(({ k, label, icon: Icon, cls }) => (
          <div key={k} data-testid={`biz-${k}`} className={`rounded-xl border p-3 bg-zinc-950/60 ${cls}`}>
            <div className="flex items-center gap-2 text-[10px] font-mono-caps uppercase tracking-widest"><Icon className="h-3.5 w-3.5" /> {label}</div>
            <div className="font-display text-3xl font-black tabular-nums mt-1">{data.counts?.[k] ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div data-testid="biz-overdue" className="rounded-xl border border-red-800 bg-red-950/20 p-3 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <div><div className="text-[10px] font-mono-caps uppercase text-red-200">Mensalidades atrasadas</div><div className="font-display text-xl font-black text-red-200">{data.overdue}</div></div>
        </div>
        <div data-testid="biz-tickets" className="rounded-xl border border-amber-800 bg-amber-950/20 p-3 flex items-center gap-3">
          <Ticket className="h-5 w-5 text-amber-300" />
          <div><div className="text-[10px] font-mono-caps uppercase text-amber-200">Chamados abertos</div><div className="font-display text-xl font-black text-amber-200">{data.tickets_open}</div></div>
        </div>
        <div data-testid="biz-addon-requests" className="rounded-xl border border-sky-800 bg-sky-950/20 p-3 flex items-center gap-3">
          <Users className="h-5 w-5 text-sky-300" />
          <div><div className="text-[10px] font-mono-caps uppercase text-sky-200">Pedidos de pacote</div><div className="font-display text-xl font-black text-sky-200">{data.pending_addon_requests}</div></div>
        </div>
      </div>
      {data.trials_ending?.length > 0 && (
        <div data-testid="biz-trials-ending" className="rounded-2xl border border-sky-800 bg-sky-950/20 p-3">
          <div className="font-mono-caps text-xs text-sky-200 uppercase tracking-widest mb-2">Testes terminando em 7 dias</div>
          <ul className="space-y-1.5">
            {data.trials_ending.map((t) => {
              const text = `Olá ${t.owner_name || ""}, seu teste grátis do AutoOficina termina em ${fmtD(t.trial_end)}. Posso ajudar com a ativação?`;
              const wa = waUrl(t.owner_phone, text);
              return (
                <li key={t.shop_id} data-testid={`trial-${t.shop_id}`} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate"><strong>{t.shop_name}</strong> · termina {fmtD(t.trial_end)}</span>
                  {wa && <a data-testid={`trial-wa-${t.shop_id}`} href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-7 px-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold"><MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp</a>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
